import "./groupCode.css";
import React, { useEffect, useState, useCallback, Suspense } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Form, Button } from "react-bootstrap";
import { useSessionContext } from "hooks/useSession";
import { colors } from "utils";
import { logTechCheck } from "utils/sentryLogger";

// Lazy-load the TechCheck tree so the overlay + its primitives + audio asset
// are only downloaded when the user actually clicks "I need help". Named
// export is preserved via .then(m => ({ default: ... })).
const TechCheckFlow = React.lazy(() =>
  import("./TechCheck").then((m) => ({ default: m.TechCheckFlow }))
);

const ENTRY_POINT = "device_check_help_button";

export const SessionTrigger = ({ isLoading, isError, setError, isSpectator, isSessionReady, hasDevices = true, hasMicInput = true }) => {
  const { setIsStart, setIsStartObserving } = useSessionContext();
  const [isShowStartButton, setIsShowStartButton] = useState(false);
  const [showTechCheck, setShowTechCheck] = useState(false);
  const [showAutoTooltip, setShowAutoTooltip] = useState(true);
  const [isHoveringButton, setIsHoveringButton] = useState(false);

  // Base readiness: timer elapsed, session prefetched, devices available
  const isBaseReady = isShowStartButton && isSessionReady && hasDevices;
  // Fully ready only after mic sound is also detected
  const isButtonReady = isBaseReady && hasMicInput;

  // Show tooltip when: button shows "I'm Ready!" but is disabled due to mic,
  // either auto-shown or on hover (after user has dismissed auto-tooltip via "I need help")
  const needsMicFix = isBaseReady && !hasMicInput;
  const showTooltip = needsMicFix && (showAutoTooltip || isHoveringButton);

  const handleHelpClick = useCallback(() => {
    // Log before mount so a crash during mount is still attributable to the click.
    logTechCheck("tech_check_help_button_clicked", {
      event: "tech_check_help_button_clicked",
      entryPoint: ENTRY_POINT,
    });
    setShowAutoTooltip(false);
    setShowTechCheck(true);
  }, []);

  const handleCloseTechCheck = useCallback(() => {
    setShowTechCheck(false);
  }, []);

  useEffect(() => {
    // Don't display start button right away to give some time for the video track to display.
    // This way users would more intuitively interact with media devices settings if they need to select another device.
    // Also, starting a session too fast while the local tracks are being mounted in the first place
    // could result in a potential race condition inside LiveKit's publishTrack method.
    setTimeout(() => {
      setIsShowStartButton(true)
    }, 2500)
  })

  const onSubmit = (e) => {
    if (!isError) {
      e.preventDefault();
      if (isSpectator) {
        setError(null)
        setIsStartObserving(true);
      } else {
        setError(null)
        setIsStart(true);
      }
    }
  };

  const buttonStyle = {
    backgroundColor: colors.pink700,
    color: "#fff",
    border: "none",
    borderRadius: "20px",
    fontSize: "16px",
    height: "48px",
    width: "205px" };
  const buttonText = isBaseReady ? (isSpectator ? "Start observing" : "I'm Ready!") : "";
  const isStillLoading = !isBaseReady;

  return (
    isLoading ? (
      <div style={{ alignSelf: "center" }}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
      </div>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "32px", alignItems: "flex-start" }}>
        <ButtonRow>
          <ButtonWrapper
            onMouseEnter={() => setIsHoveringButton(true)}
            onMouseLeave={() => setIsHoveringButton(false)}
          >
            <Form className="drop-in" onSubmit={onSubmit}>
              <Button
                disabled={!isButtonReady}
                type="submit"
                size="lg"
                aria-label={buttonText ? undefined : "Submit"}
                style={buttonStyle}>{buttonText}
                {isStillLoading &&
                  <FontAwesomeIcon icon={faSpinner} spin />
                }
              </Button>
            </Form>
            {showTooltip && (
              <Tooltip>
                Please click on the "I need help" text for tips on fixing your microphone and speaker.
              </Tooltip>
            )}
          </ButtonWrapper>
          <HelpButton type="button" onClick={handleHelpClick}>
            I need help
          </HelpButton>
        </ButtonRow>
        {showTechCheck && (
          <Suspense fallback={null}>
            <TechCheckFlow onClose={handleCloseTechCheck} entryPoint={ENTRY_POINT} />
          </Suspense>
        )}
      </div>
    )
  );
};

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ButtonWrapper = styled.div`
  position: relative;
`;

const Tooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  background: ${colors.nightlyWoods500};
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  padding: 10px 14px;
  border-radius: 8px;
  width: 240px;
  text-align: center;
  pointer-events: none;
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: ${colors.nightlyWoods500};
  }
`;

const HelpButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 20px;
  color: ${colors.pink700};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 1rem;
  height: 48px;

  &:hover {
    background: rgba(254, 30, 126, 0.08);
  }
`;
